import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  Fade,
  Divider
} from '@mui/material';
import {
  PlayArrow,
  Download,
  Clear,
  Add,
  Delete
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSectionStyles } from '@/styles/utilities/hooks';

/**
 * FMO Generate Inputs Component
 * Combines configuration queue management and generation progress
 * Owns its own section header following the established pattern
 */
const FMOGenerateInputs = ({
  configurationQueue,
  clusterspecs,
  pocketspecs,
  inputspecs,
  availableClusters,
  selectedRows,
  fileAnalysis,
  isProcessing,
  processingStatus,
  showDownload,
  generatedEntryIds,
  zipfileId,
  defaultZipfileName,
  onSelectConfiguration,
  onRemoveConfiguration,
  onAddNewConfiguration,
  isDuplicateConfiguration,
  getUniqueConfigurations,
  onGenerateInputFiles,
  onDownloadZip,
  onClearDownload
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  const [zipfileName, setZipfileName] = useState('');

  // Update zipfile name when default changes
  useEffect(() => {
    setZipfileName(defaultZipfileName || 'fmo_inputs.zip');
  }, [defaultZipfileName]);

  // Check if a configuration is complete
  const isConfigurationComplete = (config) => {
    return config.selectedPocketspec && config.selectedInputspec && 
      ((config.clusterSelectionMode === 'existing' && config.selectedCluster) ||
       (config.clusterSelectionMode === 'clusterspec' && config.selectedClusterspec));
  };

  // Generate default cluster name
  const generateDefaultClusterName = (config) => {
    const clusterspecName = clusterspecs.find(s => s.id_ === config.selectedClusterspec)?.name || '';
    const today = new Date().toISOString().slice(2, 10).replace(/-/g, '').slice(0, 6);
    
    if (fileAnalysis.targets.length > 0) {
      const firstTarget = fileAnalysis.targets[0];
      const targetRows = selectedRows.filter(row => row.target === firstTarget);
      const batches = [...new Set(
        targetRows
          .map(row => row.batch)
          .filter(Boolean)
          .filter(b => b !== '<none>')
      )];
      
      const batchPart = batches.length === 1 && batches[0] 
        ? batches[0] 
        : batches.length > 1 
          ? 'mixed' 
          : `${targetRows.length}files`;
      
      return `${today}_${firstTarget}_${batchPart}_${clusterspecName}`;
    } else {
      return `${today}_${clusterspecName}`;
    }
  };

  // Get display names for configuration
  const getConfigurationDisplayNames = (config) => {
    const clusterspecName = clusterspecs.find(s => s.id_ === config.selectedClusterspec)?.name || '';
    const clusterDisplayName = config.clusterSelectionMode === 'existing' 
      ? availableClusters.find(c => c.id_ === config.selectedCluster)?.name || ''
      : config.clusterName || generateDefaultClusterName(config);
    const pocketspecName = pocketspecs.find(s => s.id_ === config.selectedPocketspec)?.name || '';
    const inputspecName = inputspecs.find(s => s.id_ === config.selectedInputspec)?.name || '';
    
    return { clusterDisplayName, pocketspecName, inputspecName };
  };

  // Check if can add new configuration
  const canAddNewConfiguration = () => {
    const activeConfig = configurationQueue.find(c => c.isActive);
    const isActiveIncomplete = activeConfig ? !isConfigurationComplete(activeConfig) : true;
    const hasDuplicates = configurationQueue.some(config => isDuplicateConfiguration(config));
    return !isActiveIncomplete && !hasDuplicates;
  };

  // Get configuration statistics
  const getConfigurationStats = () => {
    const uniqueCount = getUniqueConfigurations(configurationQueue).length;
    const totalCount = configurationQueue.filter(c => isConfigurationComplete(c)).length;
    const duplicateCount = totalCount - uniqueCount;
    
    return { uniqueCount, totalCount, duplicateCount };
  };

  const stats = getConfigurationStats();
  const canAdd = canAddNewConfiguration();
  const activeConfig = configurationQueue.find(c => c.isActive);
  const isActiveIncomplete = activeConfig ? !isConfigurationComplete(activeConfig) : true;
  const hasDuplicates = configurationQueue.some(config => isDuplicateConfiguration(config));

  return (
    <Box sx={sectionStyles.sectionContainer}>
      {/* GENERATE INPUTS Section Header */}
      <Typography variant="caption" sx={sectionStyles.sectionHeader}>
        GENERATE INPUTS ({configurationQueue.length} CONFIGURATION{configurationQueue.length !== 1 ? 'S' : ''})
      </Typography>

      {/* Configuration Queue */}
      <Box sx={{ 
        border: '1px solid', 
        borderColor: theme.palette.divider, 
        borderRadius: theme.spacing(0.5),
        maxHeight: theme.spacing(18.75), // 150px
        overflow: 'auto',
        backgroundColor: theme.palette.background.paper,
        p: theme.spacing(0.5)
      }}>
        {configurationQueue.map((config, index) => {
          const { clusterDisplayName, pocketspecName, inputspecName } = getConfigurationDisplayNames(config);
          const isComplete = isConfigurationComplete(config);
          const isDuplicate = isDuplicateConfiguration(config);
          
          return (
            <Box 
              key={config.id}
              onClick={() => onSelectConfiguration(config.id)}
              sx={{ 
                px: theme.spacing(1),
                py: theme.spacing(0.5),
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1),
                cursor: 'pointer',
                backgroundColor: config.isActive ? theme.palette.states.active : 'transparent',
                '&:hover': {
                  backgroundColor: config.isActive ? theme.palette.selection.backgroundHover : theme.palette.action.hover
                },
                borderRadius: theme.spacing(0.5),
                border: config.isActive ? '1px solid' : '1px solid transparent',
                borderColor: config.isActive ? theme.palette.states.activeBorder : 'transparent',
                opacity: isDuplicate ? 0.8 : 1
              }}
            >
              <Typography sx={{ 
                flex: 1, 
                fontSize: theme.typography.fontSize.xs,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: isComplete ? theme.palette.text.primary : theme.palette.text.secondary,
                fontStyle: !isComplete ? 'italic' : 'normal'
              }}>
                <strong>#{index + 1}.</strong>{' '}
                {!isComplete ? (
                  config.isActive ? 'Configure settings below...' : 'Incomplete configuration'
                ) : (
                  <>
                    {isDuplicate && '↔️ '}
                    {clusterDisplayName}, {pocketspecName}, {inputspecName}
                  </>
                )}
              </Typography>
              {configurationQueue.length > 1 && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveConfiguration(config.id);
                  }}
                  sx={{ p: theme.spacing(0.25) }}
                >
                  <Delete sx={{ fontSize: theme.dimensions.icon.small }} />
                </IconButton>
              )}
            </Box>
          );
        })}
        
        {/* Add New Configuration Button */}
        <Box 
          onClick={canAdd ? onAddNewConfiguration : undefined}
          sx={{ 
            px: theme.spacing(1),
            py: theme.spacing(0.5),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            cursor: canAdd ? 'pointer' : 'not-allowed',
            opacity: canAdd ? 1 : 0.5,
            '&:hover': {
              backgroundColor: canAdd ? theme.palette.action.hover : 'transparent'
            },
            borderRadius: theme.spacing(0.5),
            mt: theme.spacing(0.5)
          }}
        >
          <Add sx={{ fontSize: theme.dimensions.icon.small, color: canAdd ? theme.palette.primary.main : theme.palette.text.disabled }} />
          <Typography sx={{ 
            fontSize: theme.typography.fontSize.xs,
            color: canAdd ? theme.palette.primary.main : theme.palette.text.disabled
          }}>
            {hasDuplicates ? 'Resolve duplicates first' : isActiveIncomplete ? 'Complete current configuration first' : 'Add another configuration'}
          </Typography>
        </Box>
      </Box>

      {/* Divider for visual separation */}
      <Divider sx={sectionStyles.sectionDivider} />

      {/* Generation Progress Section - Fixed-height container to prevent layout shift */}
      <Box sx={{ 
          minHeight: theme.spacing(7), // Reduced height, still density-aware
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
          // No bottom margin needed - parent sectionContainer has gap
        }}>
          {/* State 1: Input field */}
          {!isProcessing && !showDownload && (
            <Fade in={!isProcessing && !showDownload}>
              <TextField
                fullWidth
                size="small"
                label="ZIP Filename"
                value={zipfileName}
                onChange={(e) => setZipfileName(e.target.value)}
                helperText="Name for the ZIP file containing FMO input files"
                sx={{ m: 0 }} // Remove any default margins
              />
            </Fade>
          )}
          
          {/* State 2: Processing */}
          {isProcessing && (
            <Fade in={isProcessing}>
              <Box sx={{ 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: theme.spacing(7) // Match the container height
              }}>
                <CircularProgress size={24} sx={{ margin: '0 auto', mb: theme.spacing(1) }} />
                <Typography variant="caption" sx={{ 
                  display: 'block',
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.fontSize.sm
                }}>
                  {processingStatus || 'Processing...'}
                </Typography>
              </Box>
            </Fade>
          )}
          
          {/* State 3: Success message */}
          {showDownload && generatedEntryIds.length > 0 && (
            <Fade in={showDownload}>
              <Alert severity="success" sx={{ 
                py: theme.spacing(0.75),
                my: 0, // Remove default Alert margins
                alignSelf: 'flex-start',
                width: '100%'
              }}>
                <Typography variant="caption" sx={{ fontWeight: theme.typography.fontWeight.semibold, display: 'block' }}>
                  ✓ Successfully generated {generatedEntryIds.length} FMO input file{generatedEntryIds.length !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: theme.typography.fontSize.xs, mt: theme.spacing(0.25) }}>
                  ZIP file: "{zipfileName}"
                </Typography>
              </Alert>
            </Fade>
          )}
      </Box>
      
      {/* Dynamic buttons below the fixed box */}
      <Box>
        {/* State 1: Generate button */}
        {!isProcessing && !showDownload && (
          <Button
              variant="contained"
              size="medium"
              fullWidth
              startIcon={<PlayArrow sx={{ fontSize: theme.dimensions.icon.medium }} />}
              onClick={() => onGenerateInputFiles(zipfileName)}
              disabled={
                selectedRows.length === 0 || 
                fileAnalysis.missingTargetCount > 0 ||
                getUniqueConfigurations(configurationQueue).length === 0 ||
                !zipfileName || !zipfileName.trim()
              }
              sx={{
                height: theme.dimensions.button.medium,
                fontSize: theme.typography.fontSize.base
              }}
          >
            Generate FMO Input Files
          </Button>
        )}
        
        {/* State 2: Processing button */}
        {isProcessing && (
          <Button
              variant="contained"
              size="medium"
              fullWidth
              disabled
              sx={{
                height: theme.dimensions.button.medium,
                fontSize: theme.typography.fontSize.base
              }}
          >
            Processing...
          </Button>
        )}
        
        {/* State 3: Download and Clear buttons */}
        {showDownload && generatedEntryIds.length > 0 && (
          <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
              <Button
                variant="contained"
                size="medium"
                startIcon={<Download sx={{ fontSize: theme.dimensions.icon.medium }} />}
                onClick={() => onDownloadZip(zipfileId, zipfileName)}
                sx={{ 
                  flex: 1,
                  height: theme.dimensions.button.medium,
                  fontSize: theme.typography.fontSize.base,
                  backgroundColor: theme.palette.success.main,
                  '&:hover': {
                    backgroundColor: theme.palette.success.dark
                  }
                }}
              >
                Download ZIP File
              </Button>
              
              <Button
                variant="outlined"
                size="medium"
                startIcon={<Clear sx={{ fontSize: theme.dimensions.icon.medium }} />}
                onClick={() => {
                  onClearDownload();
                  setZipfileName(defaultZipfileName || 'fmo_inputs.zip');
                }}
                sx={{ 
                  minWidth: theme.spacing(10),
                  height: theme.dimensions.button.medium,
                  fontSize: theme.typography.fontSize.base
                }}
              >
                Clear
              </Button>
          </Box>
        )}
      </Box>
        
      
      {/* Error messages only - outside the fixed box */}
      {processingStatus && processingStatus.includes('Error') && !isProcessing && (
        <Alert severity="error" sx={{ mt: theme.spacing(1) }}>
          {processingStatus}
        </Alert>
      )}
    </Box>
  );
};

export default FMOGenerateInputs;